/**
 * @file Hook to expand player URLs with context-aware base paths
 * @remarks
 * Resolves the Expo Router limitation where relative paths don't work
 * as expected when there are same-named routes at different hierarchy levels.
 * This hook detects the current navigation context (watchlist, search, etc.)
 * and prepends the appropriate base path to player URLs.
 */

import { usePathname } from "expo-router";
import { useCallback, useMemo } from "react";

/**
 * Base paths that should be prepended to player URLs when in their context
 */
const CONTEXT_BASE_PATHS = ["/watchlist", "/search"] as const;

/**
 * Normalizes a URL by ensuring it has a leading slash and no double slashes
 * @param url - The URL to normalize
 * @returns Normalized URL with single leading slash
 */
function normalizeUrl(url: string): string {
  const trimmed = url.replace(/^\/+/, "");
  return `/${trimmed}`;
}

/**
 * Determines the base path from the current pathname
 * @param pathname - Current route pathname
 * @returns The matching base path or empty string
 */
export function getBasePath(pathname: string): string {
  const matchingPath = CONTEXT_BASE_PATHS.find(basePath =>
    pathname.startsWith(basePath)
  );
  return matchingPath ?? "";
}

/**
 * Expands a URL with the appropriate context base path
 * @param pathname - Current route pathname
 * @param url - The URL to expand
 * @returns Expanded URL with context prefix if applicable
 */
export function expandPlayerUrl(pathname: string, url: string): string {
  const basePath = getBasePath(pathname);
  const normalizedUrl = normalizeUrl(url);
  return basePath ? `${basePath}${normalizedUrl}` : normalizedUrl;
}

/**
 * Hook that expands player URLs with the appropriate context base path
 * @param url - The player URL to expand (e.g., "/players/123/compare")
 * @returns The expanded URL with context prefix (e.g., "/watchlist/players/123/compare")
 * @example
 * // When current pathname is "/watchlist"
 * const expandedUrl = useExpandedPlayerUrl("/players/123/compare");
 * // Returns: "/watchlist/players/123/compare"
 * @example
 * // When current pathname is "/dashboard"
 * const expandedUrl = useExpandedPlayerUrl("/players/123/compare");
 * // Returns: "/players/123/compare"
 */
function useExpandedPlayerUrl(url: string): string {
  const pathname = usePathname();

  return useMemo(() => expandPlayerUrl(pathname, url), [pathname, url]);
}

/**
 * Hook that returns a function to expand player URLs with context base path
 * @remarks
 * Use this hook when you need to expand URLs with dynamic player IDs
 * that are not known at component render time (e.g., in callbacks).
 * @returns Function that takes a URL and returns the expanded URL
 * @example
 * const getExpandedUrl = useGetExpandedPlayerUrl();
 * const handlePlayerClick = (playerId: string) => {
 *   router.push(getExpandedUrl(`/players/${playerId}`));
 * };
 */
export function useGetExpandedPlayerUrl(): (url: string) => string {
  const pathname = usePathname();

  return useCallback(
    (url: string) => expandPlayerUrl(pathname, url),
    [pathname]
  );
}

export default useExpandedPlayerUrl;
